import React, {useEffect, useRef} from 'react';
import {
    generatePayNum, initPayAttempt, notifyPaid, notifyCancel, sendErrorLog, PAY_USAGE,
    recheckPaypalStatus, judgePaypalStatus, watchPaypalOverlays,
} from "../../../services/PayGuard";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  Paypal 결제버튼 (Paypal Javascript SDK)
 * ════════════════════════════════════════════════════════════════════════════════
 *  [결제누락 개선]
 *   1) 버튼 중복렌더 차단 : 기존에는 useEffect([total]) 안에서 render()만 하고 cleanup 이 없어
 *                          total 이 바뀔 때마다 페이팔 버튼 인스턴스가 누적 생성되었다.
 *                          -> 렌더 1회 보장 + 언마운트 시 close() 정리
 *   2) 중복 capture 차단  : capture 진행중/완료 여부를 ref 로 잠가 이중결제를 원천 차단
 *   3) 서버측 중복 확인   : 결제성공 즉시 결제원장에 PAID 를 기록하고, 이미 기록된 건이면 중복으로 간주해 중단
 *   4) 실제 거래ID 전달   : PayPal order.id 를 상위로 전달하여 대사(reconciliation) 가 가능하도록 함
 *   5) onCancel/onError   : 기존에는 console 출력만 했으나 사용자 안내 + 결제원장 CANCEL 기록
 * ════════════════════════════════════════════════════════════════════════════════
 */
function PaypalPaymentBt({ total, processAfterPaypal, currency, usageType, finalPayExpense, onPayError, onPaidUncertain }) {

    const paypalRef = useRef();
    /** 결제식별번호 (= PayPal reference_id). 컴포넌트 수명동안 1개만 유지한다. */
    const payNumRef = useRef(generatePayNum());
    /** 버튼 인스턴스 (언마운트 시 정리용) */
    const buttonsRef = useRef(null);
    /** capture 중복호출 차단 잠금 */
    const capturingRef = useRef(false);
    /** 이미 결제완료 처리된 경우 재처리 차단 */
    const capturedRef = useRef(false);
    /** 렌더 1회 보장 */
    const renderedRef = useRef(false);

    // 버튼은 1회만 렌더하되 금액/통화는 항상 최신값을 사용해야 하므로 ref 로 동기화한다.
    const totalRef = useRef(total);
    const currencyRef = useRef(currency);
    const finalPayExpenseRef = useRef(finalPayExpense);
    totalRef.current = total;
    currencyRef.current = currency;
    finalPayExpenseRef.current = finalPayExpense;

    /** 엔화는 소숫점을 허용하지 않음 */
    const getConvertedPrice = () => {
        const amount = totalRef.current;
        return currencyRef.current === "JPY"
            ? (String(amount).includes('.') ? String(amount).split('.')[0] : amount)
            : amount;
    };

    useEffect(() => {
        if (renderedRef.current) return;   // ✅ 중복 렌더 차단
        if (!window.paypal || !paypalRef.current) return;
        renderedRef.current = true;

        const paypalOrderId = payNumRef.current;

        /**
         * 결제확정 처리 — 결제된 것이 확실할 때만 부른다.
         */
        /**
         * ══════════════════════════════════════════════════════════════════════
         *  [근본원인] SDK 뒷정리를 기다린 뒤에 상위로 넘긴다
         * ══════════════════════════════════════════════════════════════════════
         *  페이팔 SDK 는 결제창을 띄울 때 화면 전체를 덮는 요소를 <body> 에 붙인다.
         *    <div id="paypal-overlay-uid_xxx" class="paypal-checkout-sandbox"
         *         style="position:fixed; z-index:2147483647">
         *  이 요소는 SDK 가 스스로 걷어가는데, 그 정리가 끝나기 전에
         *  우리가 팝업을 닫고 다음 화면으로 넘어가면 버튼이 먼저 사라져
         *  덮개만 <body> 에 고아로 남는다.
         *
         *  React 라우팅은 <body> 를 건드리지 않으므로 화면만 바뀌고 덮개는 그대로다.
         *  그래서 결제완료 화면의 버튼이 눌리지도, 손모양이 나오지도 않았다.
         *  (새로고침해야 사라졌던 이유)
         *
         *  그러니 '치우는 것' 이 아니라 '고아로 만들지 않는 것' 이 근본 해결이다.
         *  close() 가 끝날 때까지 기다린 뒤에 상위로 넘긴다.
         *  SDK 가 응답하지 않는 경우를 대비해 최대 대기시간을 둔다.
         */
        const closeSdkAndWait = () => new Promise((resolve) => {
            let done = false;
            const finish = () => {
                if (done) return;
                done = true;
                resolve();
            };
            // 정리가 늦어져도 결제 후속처리가 멈추면 안 된다
            setTimeout(finish, 1500);
            try {
                const closing = buttonsRef.current && buttonsRef.current.close
                    ? buttonsRef.current.close()
                    : null;
                if (closing && typeof closing.then === 'function') {
                    closing.then(finish, finish);
                } else {
                    finish();
                }
            } catch (e) {
                finish();
            }
        });

        const settlePaid = async (status, pgTid) => {
            capturedRef.current = true;
            // ✅ 서버에 결제성공을 먼저 확정기록. 이미 기록되어 있으면(=중복 통보) 후속처리를 중단한다.
            const duplicated = await notifyPaid({payNum: paypalOrderId, pgTid});
            if (duplicated) {
                console.log("Paypal 중복결제 통보 감지 - 후속 주문저장 생략");
                return;
            }
            // SDK 가 자기 덮개를 걷어갈 때까지 기다린 뒤에 화면을 넘긴다
            await closeSdkAndWait();
            processAfterPaypal(status, paypalOrderId, pgTid);
        };

        /**
         * 결제여부를 알 수 없을 때 — 주문서를 잃지 않도록 '결제대기' 강제저장으로 넘긴다.
         *  실제로 결제되지 않았더라도 [Save] 로 저장한 것과 같은 상태라 위험하지 않고,
         *  실제로 결제되었다면 주문서 통째 유실을 막는다.
         *  상위가 이 갈래를 받지 않는 화면(예치금충전)이면 기존처럼 안내문구만 띄운다.
         */
        const settleUncertain = async (pgTid, reason) => {
            capturedRef.current = true;
            await sendErrorLog("WebUser", "Paypal 결제여부 불명", `${reason} / PayNum: ${paypalOrderId}`);
            if (onPaidUncertain) onPaidUncertain(paypalOrderId, pgTid, reason);
            else if (onPayError) onPayError();
        };

        const buttons = window.paypal.Buttons({
            createOrder: async (data, actions) => {
                const convertToPrice = getConvertedPrice();
                // ✅ 결제팝업이 뜨기 전에 결제원장을 선기록해둔다.
                //    이후 결제가 성공하면(웹훅/프론트 통보) 이 행이 PAID 로 바뀌므로,
                //    주문서 저장이 실패해도 '결제됐는데 주문 없음' 누락건을 반드시 탐지할 수 있다.
                await initPayAttempt({
                    payNum: paypalOrderId,
                    payWay: 'PM01',
                    usageType: usageType || PAY_USAGE.ORDER,
                    amount: finalPayExpenseRef.current,
                    forAmount: convertToPrice,
                    forCurrency: currencyRef.current,
                });

                return actions.order.create({
                    purchase_units: [{
                        amount: {
                            currency_code: currencyRef.current,
                            value: convertToPrice
                        },
                        reference_id: paypalOrderId
                    }]
                });
            },
            onApprove: async (data, actions) => {
                // ✅ 이중 capture 차단
                if (capturingRef.current || capturedRef.current) {
                    console.log("Paypal capture 중복호출 차단");
                    return;
                }
                capturingRef.current = true;
                const approvedOrderId = data?.orderID || null;

                try {
                    const order = await actions.order.capture();
                    const pgTid = order?.id || approvedOrderId;
                    const judged = judgePaypalStatus(order?.status);

                    if (judged === 'paid') {
                        await settlePaid(order.status, pgTid);
                    } else if (judged === 'notPaid') {
                        // 돈이 빠지지 않은 것이 확실하다 -> 기존처럼 결제실패 안내만 (주문서 저장 안 함)
                        await sendErrorLog("WebUser", "Paypal Approve Failed", `Status: ${order?.status}, PayNum: ${paypalOrderId}`);
                        processAfterPaypal("FAILED", paypalOrderId, pgTid);
                    } else {
                        // 처음 보는 상태값 -> 결제여부 모름
                        await settleUncertain(pgTid, `capture 응답상태 판정불가: ${order?.status}`);
                    }
                } catch (error) {
                    const reason = error?.message || String(error);
                    await sendErrorLog("WebUser", "Paypal Capture Error", `${reason} / PayNum: ${paypalOrderId}`);

                    /*
                     * [결제누락 개선] capture 응답을 못 받았다.
                     *  이 순간 돈이 빠졌는지 알 수 없으므로, 서버를 통해 페이팔에 한 번 더 물어본다.
                     *  같은 고객이 어떤 때는 성공하고 어떤 때는 실패하던 불규칙한 사고가
                     *  대부분 이 구간(캡처 성공 직후 응답 유실)이라 여기서 구제된다.
                     */
                    const status = await recheckPaypalStatus(approvedOrderId);
                    const judged = judgePaypalStatus(status);

                    if (judged === 'paid') {
                        console.log("[결제누락 개선] capture 응답은 유실됐지만 실제로는 결제완료 -> 정상 진행");
                        await settlePaid(status, approvedOrderId);
                    } else if (judged === 'notPaid') {
                        if (onPayError) onPayError();
                    } else {
                        await settleUncertain(approvedOrderId, `capture 실패 + 상태확인 실패: ${reason}`);
                    }
                } finally {
                    capturingRef.current = false;
                }
            },
            onCancel: async () => {
                // 사용자가 페이팔 창에서 결제를 취소한 경우
                await notifyCancel({payNum: paypalOrderId, failReason: 'User canceled on PayPal'});
            },
            onError: async (err) => {
                console.error("Payment Error!", err);
                await sendErrorLog("WebUser", "Paypal SDK Error", `${err?.message || err} / PayNum: ${paypalOrderId}`);
                await notifyCancel({payNum: paypalOrderId, failReason: `PayPal SDK Error: ${err?.message || err}`});
                if (onPayError) onPayError();
            }
        });

        buttonsRef.current = buttons;
        buttons.render(paypalRef.current);

        return () => {
            // ✅ 언마운트 시 버튼 인스턴스 정리 (중복 렌더/유령 버튼 방지)
            try {
                if (buttonsRef.current && buttonsRef.current.close) {
                    buttonsRef.current.close();
                }
            } catch (e) {
                /* 이미 닫힌 경우 무시 */
            }
            buttonsRef.current = null;

            /*
             * SDK 가 <body> 에 붙여둔 덮개를 치운다.
             *  실제로 남아있던 것 :
             *    <div id="paypal-overlay-uid_xxx" class="paypal-checkout-sandbox"
             *         style="position:fixed; z-index:2147483647">
             *  화면 전체를 덮어 다음 화면의 버튼이 눌리지 않는다.
             *  늦게/여러 번 붙기도 해서 잠시 감시하며 치운다.
             *  (이동한 화면이 무엇이든 통하도록 '만든 쪽' 에서 치운다)
             */
            watchPaypalOverlays();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <div ref={paypalRef}></div>
        </div>
    );
}

export default PaypalPaymentBt;
